import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WebsiteDetails } from "Pages/MyWebiste/types";

type ImageType = string | null | undefined;

interface TestimonialsDetails {
  id: number;
  clientsImage: any;
  name: string;
  twitterUsername: string;
  twitterLink: string;
  clientsReview: string;
  createdAt: string;
  publishSetting: number;
}

export interface CustomAgreement {
  id: number;
  isFeePlan: boolean;
  isModelPortfolio: boolean;
  title: string;
  content: string;
  createdAt: string;
  company: number;
  name?: string;
  value?: number | string;
}

export type PreviewMode = "desktop" | "tablet" | "mobile";

const initialState: {
  selectedSection: string | null;
  openExtraSection: string | null;
  selectedImage: ImageType;
  croppedImage: ImageType;
  croppedYoutubeThumbnail: ImageType;
  croppedAdvisorImage: ImageType;
  selectedAdvisorImage: ImageType;
  selectedYoutubeThumbnail: ImageType;
  selectedQuoteImage: ImageType;
  croppedQuoteImage: ImageType;
  websiteDetails: WebsiteDetails;
  openTestimonialModal: boolean;
  testimonialDetails: TestimonialsDetails;
  customAgreementTemplates: CustomAgreement[];
  previewMode: PreviewMode;
  focusedField: string | null;
} = {
  selectedSection: null,
  openExtraSection: null,
  selectedImage: null,
  croppedImage: null,
  croppedYoutubeThumbnail: null,
  croppedAdvisorImage: null,
  selectedAdvisorImage: null,
  selectedYoutubeThumbnail: null,
  selectedQuoteImage: null,
  croppedQuoteImage: null,
  websiteDetails: null,
  openTestimonialModal: false,
  testimonialDetails: null,
  customAgreementTemplates: [],
  previewMode: "desktop",
  focusedField: null,
};

const myWebsiteSlice = createSlice({
  name: "myWebsite",
  initialState,
  reducers: {
    setSelectedSection: (state, action) => {
      state.selectedSection = action.payload;
    },
    setOpenExtraSection: (state, action) => {
      state.openExtraSection = action.payload;
    },
    setSelectedImage: (state, action) => {
      state.selectedImage = action.payload;
    },
    setCroppedImage: (state, action) => {
      state.croppedImage = action.payload;
    },
    setCroppedYoutubeThumbnail: (state, action) => {
      state.croppedYoutubeThumbnail = action.payload;
    },
    setSelectedYoutubeThumbnail: (state, action) => {
      state.selectedYoutubeThumbnail = action.payload;
    },
    setSelectedQuoteImage: (state, action) => {
      state.selectedQuoteImage = action.payload;
    },
    setCroppedQuoteImage: (state, action) => {
      state.croppedQuoteImage = action.payload;
    },
    setCroppedAdvisorImage: (state, action) => {
      state.croppedAdvisorImage = action.payload;
    },
    setSelectedAdvisorImage: (state, action) => {
      state.selectedAdvisorImage = action.payload;
    },
    setWebsiteDetails: (state, action) => {
      state.websiteDetails = action.payload;
    },
    setOpenTestimonialModal: (state, action) => {
      state.openTestimonialModal = action.payload;
    },
    setTestimonialDetails: (state, action) => {
      state.testimonialDetails = action.payload;
    },
    setCustomAgreementTemplates: (state, action) => {
      state.customAgreementTemplates = action.payload?.map(
        (item: CustomAgreement) => ({
          ...item,
          name: item.title,
          value: item.id,
        }),
      );
    },
    setPreviewMode: (state, action: PayloadAction<PreviewMode>) => {
      state.previewMode = action.payload;
    },
    setFocusedField: (state, action) => {
      state.focusedField = action.payload;
    },
  },
});

export const {
  setSelectedSection,
  setOpenExtraSection,
  setSelectedImage,
  setCroppedImage,
  setCroppedYoutubeThumbnail,
  setSelectedYoutubeThumbnail,
  setSelectedQuoteImage,
  setCroppedQuoteImage,
  setCroppedAdvisorImage,
  setSelectedAdvisorImage,
  setWebsiteDetails,
  setOpenTestimonialModal,
  setTestimonialDetails,
  setCustomAgreementTemplates,
  setPreviewMode,
  setFocusedField,
} = myWebsiteSlice.actions;

export default myWebsiteSlice.reducer;
